import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "ai-customization-panel" }
const _hoisted_2 = { class: "ai-customization-panel__header" }
const _hoisted_3 = { class: "ai-customization-panel__header-left" }
const _hoisted_4 = { class: "ai-customization-panel__title" }
const _hoisted_5 = { class: "ai-customization-panel__body" }
const _hoisted_6 = { class: "ai-customization-panel__field" }
const _hoisted_7 = {
  key: 0,
  class: "ai-customization-panel__attachments"
}
const _hoisted_8 = { class: "ai-customization-panel__dropzone-hint" }
const _hoisted_9 = {
  key: 0,
  class: "ai-customization-panel__file-list"
}
const _hoisted_10 = { class: "ai-customization-panel__file-name" }
const _hoisted_11 = { class: "ai-customization-panel__file-size" }
const _hoisted_12 = ["onClick"]

import { Icon } from "@iconify/vue";
import { ref } from "vue";
import { useToast } from "@/composables/ui/useToast";
import Button from "@/components/ui/Button.vue";

interface Props {
  disabled?: boolean;
  title?: string;
  generateButtonLabel?: string;
  showClose?: boolean;
  showAttachments?: boolean;
  maxFiles?: number;
  maxFileSizeMB?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AiCustomizationPanel',
  props: {
    disabled: { type: Boolean, default: false },
    title: { default: "AI Refine" },
    generateButtonLabel: { default: "Generate Message" },
    showClose: { type: Boolean, default: false },
    showAttachments: { type: Boolean, default: true },
    maxFiles: { default: 3 },
    maxFileSizeMB: { default: 5 }
  },
  emits: ["generate", "close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const $toast = useToast();

const customInstructions = ref("");
const attachments = ref<File[]>([]);
const isDragOver = ref(false);
const fileInputRef = ref<HTMLInputElement | null>(null);

const emit = __emit;

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const validateAndAddFiles = (files: FileList | File[]) => {
  const maxBytes = props.maxFileSizeMB * 1024 * 1024;
  const remaining = props.maxFiles - attachments.value.length;

  if (remaining <= 0) {
    $toast?.info(`Maximum ${props.maxFiles} files allowed`, {
      position: "top-right",
      duration: 3000,
    });
    return;
  }

  const filesToAdd = Array.from(files).slice(0, remaining);

  for (const file of filesToAdd) {
    if (file.size > maxBytes) {
      $toast?.error(`"${file.name}" exceeds ${props.maxFileSizeMB}MB limit`, {
        position: "top-right",
        duration: 3000,
      });
      continue;
    }
    attachments.value.push(file);
  }
};

const handleDrop = (event: DragEvent) => {
  isDragOver.value = false;
  if (event.dataTransfer?.files) {
    validateAndAddFiles(event.dataTransfer.files);
  }
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFileSelect = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files) {
    validateAndAddFiles(input.files);
  }
  // Reset input so re-selecting the same file triggers change
  input.value = "";
};

const removeFile = (index: number) => {
  attachments.value.splice(index, 1);
};

const handleGenerate = () => {
  emit("generate", {
    customInstructions: customInstructions.value.trim(),
    attachments: [...attachments.value],
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(Icon), {
          icon: "mdi:sparkles",
          class: "ai-customization-panel__icon"
        }),
        _createElementVNode("span", _hoisted_4, _toDisplayString(__props.title), 1)
      ]),
      (__props.showClose)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            class: "ai-customization-panel__close",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close'))),
            "aria-label": "Close"
          }, [
            _createVNode(_unref(Icon), { icon: "mdi:close" })
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _cache[4] || (_cache[4] = _createElementVNode("label", { class: "ai-customization-panel__label" }, "Custom Instructions", -1)),
        _withDirectives(_createElementVNode("textarea", {
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((customInstructions).value = $event)),
          class: "ai-customization-panel__textarea",
          placeholder: "E.g., 'Mention our upcoming product launch' or 'Keep it brief and to the point'.",
          rows: "3"
        }, null, 512), [
          [_vModelText, customInstructions.value]
        ])
      ]),
      (__props.showAttachments)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _cache[6] || (_cache[6] = _createElementVNode("label", { class: "ai-customization-panel__label" }, "Attachments", -1)),
            _createElementVNode("div", {
              class: _normalizeClass(["ai-customization-panel__dropzone", { 'ai-customization-panel__dropzone--dragover': isDragOver.value }]),
              onDragover: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (isDragOver.value = true), ["prevent"])),
              onDragleave: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (isDragOver.value = false), ["prevent"])),
              onDrop: _withModifiers(handleDrop, ["prevent"]),
              onClick: triggerFileInput
            }, [
              _createVNode(_unref(Icon), {
                icon: "mdi:cloud-upload-outline",
                class: "ai-customization-panel__dropzone-icon"
              }),
              _cache[5] || (_cache[5] = _createElementVNode("span", { class: "ai-customization-panel__dropzone-text" }, [
                _createTextVNode(" Drop files here or "),
                _createElementVNode("span", { class: "ai-customization-panel__dropzone-link" }, "browse")
              ], -1)),
              _createElementVNode("span", _hoisted_8, " Max " + _toDisplayString(__props.maxFiles) + " files, " + _toDisplayString(__props.maxFileSizeMB) + "MB each ", 1)
            ], 34),
            _createElementVNode("input", {
              ref_key: "fileInputRef",
              ref: fileInputRef,
              type: "file",
              multiple: "",
              class: "ai-customization-panel__file-input",
              onChange: handleFileSelect
            }, null, 544),
            (attachments.value.length > 0)
              ? (_openBlock(), _createElementBlock("ul", _hoisted_9, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(attachments.value, (file, index) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: `${file.name}-${index}`,
                      class: "ai-customization-panel__file-item"
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:file-outline",
                        class: "ai-customization-panel__file-icon"
                      }),
                      _createElementVNode("span", _hoisted_10, _toDisplayString(file.name), 1),
                      _createElementVNode("span", _hoisted_11, _toDisplayString(formatFileSize(file.size)), 1),
                      _createElementVNode("button", {
                        type: "button",
                        class: "ai-customization-panel__file-remove",
                        onClick: ($event: any) => (removeFile(index)),
                        "aria-label": "Remove file"
                      }, [
                        _createVNode(_unref(Icon), { icon: "mdi:close-circle" })
                      ], 8, _hoisted_12)
                    ]))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      _createVNode(Button, {
        variant: "primary",
        size: "sm",
        icon: "mdi:refresh",
        loading: __props.disabled,
        disabled: __props.disabled,
        class: "ai-customization-panel__generate-btn",
        onClick: handleGenerate
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(__props.generateButtonLabel), 1)
        ]),
        _: 1
      }, 8, ["loading", "disabled"])
    ])
  ]))
}
}

})